import { Fragment, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faArrowsRotate,
	faBusinessTime,
	faChevronDown,
	faChevronRight,
	faClockRotateLeft,
	faSitemap,
	faUmbrellaBeach,
	faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { Card, CardBody, CardFooter, CardHeader, Collapse, Table } from "reactstrap";
import {
	SYNC_STEPS,
	getIssueKey,
	getSyncTriggerKey,
} from "../../../Integrations/HiBob/messages";
import { count, describeDuration, toMoment } from "./format";
import { SyncPill } from "./SummaryTiles";

const STEP_ICONS = {
	users: faUsers,
	reference: faSitemap,
	jobHistory: faClockRotateLeft,
	expectedHours: faBusinessTime,
	timeoff: faUmbrellaBeach,
};

const COLUMNS = [
	{ field: "created", key: "hibob-col-created" },
	{ field: "updated", key: "hibob-col-updated" },
	{ field: "unchanged", key: "hibob-col-unchanged" },
];

export const buildRows = (sync) => {
	const steps = Array.isArray(sync?.steps) ? sync.steps : [];
	return SYNC_STEPS.map(({ step, key }) => {
		const found = steps.find((s) => s?.step === step);
		if (!found) {
			return null;
		}
		return {
			step,
			key,
			created: count(found.created),
			updated: count(found.updated),
			unchanged: count(found.unchanged),
			issues: count(found.failed) + count(found.flagged),
			reasons: (Array.isArray(found.issues) ? found.issues : [])
				.filter((issue) => issue && count(issue.count) > 0)
				.map((issue) => ({ key: getIssueKey(issue.code), count: count(issue.count) })),
		};
	}).filter(Boolean);
};

function Num({ value }) {
	return <span className={value ? "" : "hibob-zero"}>{value}</span>;
}

function IssueReasons({ row, t }) {
	if (!row.reasons.length) {
		return null;
	}
	return (
		<ul className="hibob-issue-list" aria-label={t("hibob-col-issues")}>
			{row.reasons.map((reason) => (
				<li key={reason.key}>{t("hibob-issue-line", { count: reason.count, text: t(reason.key) })}</li>
			))}
		</ul>
	);
}

function IssuesCell({ row, running, open, onToggle, t, idPrefix }) {
	if (running) {
		return (
			<span className="spinner-border spinner-border-sm hibob-muted" role="presentation" aria-hidden="true" />
		);
	}
	if (!row.issues) {
		return (
			<span className="hibob-zero" aria-label={t("hibob-no-issues-label")}>
				{t("hibob-no-issues")}
			</span>
		);
	}
	const label = t("hibob-show-issues", { count: row.issues, step: t(row.key) });
	if (!row.reasons.length) {
		return <span className="hibob-issue-chip">{row.issues}</span>;
	}
	return (
		<button
			type="button"
			className="hibob-issue-chip"
			aria-expanded={open}
			aria-controls={`${idPrefix}-${row.step}`}
			aria-label={label}
			onClick={(e) => {
				e.stopPropagation();
				onToggle(row.step);
			}}
		>
			{row.issues}
			<FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} size="xs" aria-hidden="true" />
		</button>
	);
}

function SyncCard({ data, formats, openStep, onToggleStep, onRefresh, refreshing, paused }) {
	const { t } = useTranslation();
	const sync = data.sync || {};
	const running = sync.lastStatus === "running";
	const rows = buildRows(sync);
	const startedAt = toMoment(sync.lastStartedAt);
	const finishedAt = toMoment(sync.lastFinishedAt);
	const triggerKey = getSyncTriggerKey(sync.lastTrigger);
	const trigger = triggerKey ? t(triggerKey) : "";

	const rowRefs = useRef({});
	useEffect(() => {
		const el = openStep && rowRefs.current[openStep];
		if (el && typeof el.scrollIntoView === "function") {
			el.scrollIntoView({ behavior: "smooth", block: "center" });
		}
	}, [openStep]);

	let footer = null;
	if (startedAt) {
		footer = running || !finishedAt
			? t("hibob-sync-footer-running", { time: formats.when(startedAt), trigger })
			: t("hibob-sync-footer", {
					time: formats.when(startedAt),
					duration: describeDuration(startedAt, finishedAt, t),
					trigger,
			  });
	}

	return (
		<Card className={paused ? "hibob-paused" : undefined} data-testid="hibob-sync-card">
			<CardHeader className="d-flex align-items-center">
				<h2 className="hibob-card-title">{t("hibob-last-sync-card")}</h2>
				<span className="ml-auto">
					{paused ? (
						<span className="hibob-status is-small is-neutral">{t("hibob-paused")}</span>
					) : (
						<SyncPill status={sync.lastStatus} />
					)}
				</span>
			</CardHeader>

			{rows.length === 0 ? (
				<CardBody>
					<p className="hibob-muted mb-0">
						{sync.firstSyncPending ? t("hibob-first-sync-pending") : t("hibob-no-sync-yet")}
					</p>
				</CardBody>
			) : (
				<>
					<div className="d-none d-md-block" data-testid="hibob-sync-table">
						<Table className="hibob-table" responsive={false}>
							<thead>
								<tr>
									<th scope="col">{t("hibob-step")}</th>
									{COLUMNS.map((col) => (
										<th key={col.field} scope="col" className="hibob-num">
											{t(col.key)}
										</th>
									))}
									<th scope="col" className="hibob-num">
										{t("hibob-col-issues")}
									</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((row) => {
									const expandable = row.issues > 0 && row.reasons.length > 0 && !running;
									const open = expandable && openStep === row.step;
									return (
										<Fragment key={row.step}>
											<tr
												ref={(el) => {
													rowRefs.current[row.step] = el;
												}}
												className={expandable ? "is-expandable" : undefined}
												onClick={expandable ? () => onToggleStep(row.step) : undefined}
											>
												<th scope="row" className="hibob-step-name">
													<FontAwesomeIcon
														icon={STEP_ICONS[row.step]}
														className="hibob-step-icon"
														fixedWidth
														aria-hidden="true"
													/>
													{t(row.key)}
												</th>
												{COLUMNS.map((col) => (
													<td key={col.field} className="hibob-num">
														<Num value={row[col.field]} />
													</td>
												))}
												<td className="hibob-num">
													<IssuesCell
														row={row}
														running={running}
														open={open}
														onToggle={onToggleStep}
														t={t}
														idPrefix="hibob-issues"
													/>
												</td>
											</tr>
											{expandable && (
												<tr className="hibob-issues-row">
													<td colSpan={5} className="p-0">
														<Collapse isOpen={open} id={`hibob-issues-${row.step}`}>
															<div className="px-3 pb-2">
																<IssueReasons row={row} t={t} />
															</div>
														</Collapse>
													</td>
												</tr>
											)}
										</Fragment>
									);
								})}
							</tbody>
						</Table>
					</div>

					<ul className="hibob-stacked d-md-none" data-testid="hibob-sync-stacked">
						{rows.map((row) => {
							const expandable = row.issues > 0 && row.reasons.length > 0 && !running;
							const open = expandable && openStep === row.step;
							return (
								<li key={row.step} data-testid="hibob-stacked-row">
									<div className="d-flex align-items-center">
										<span className="hibob-step-name font-weight-bold">
											<FontAwesomeIcon
												icon={STEP_ICONS[row.step]}
												className="mr-2"
												fixedWidth
												aria-hidden="true"
											/>
											{t(row.key)}
										</span>
										<span className="ml-auto">
											<IssuesCell
												row={row}
												running={running}
												open={open}
												onToggle={onToggleStep}
												t={t}
												idPrefix="hibob-issues-m"
											/>
										</span>
									</div>
									<dl>
										{COLUMNS.map((col) => (
											<div key={col.field}>
												<dt>{t(col.key)}</dt>
												<dd>
													<Num value={row[col.field]} />
												</dd>
											</div>
										))}
									</dl>
									{expandable && (
										<Collapse isOpen={open} id={`hibob-issues-m-${row.step}`}>
											<IssueReasons row={row} t={t} />
										</Collapse>
									)}
								</li>
							);
						})}
					</ul>
				</>
			)}

			<CardFooter className="d-flex align-items-center">
				<span>{footer || t("hibob-next-sync")}</span>
				<button
					type="button"
					className="btn btn-sm btn-link hibob-link-button ml-auto"
					onClick={onRefresh}
					disabled={refreshing}
					aria-label={t("hibob-refresh")}
					title={t("hibob-refresh")}
				>
					<FontAwesomeIcon icon={faArrowsRotate} spin={refreshing} aria-hidden="true" />
				</button>
			</CardFooter>
		</Card>
	);
}

export default SyncCard;
